import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { PRODUCT_HOME_ROUTES, PRODUCT_ID } from 'src/app/config/product';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService, REGION_UNAVAILABLE_ROUTE } from 'src/app/services/organization/organization.service';
import { ProductAccessService } from 'src/app/services/product-access/product-access.service';
import { RegionService } from 'src/app/services/region/region.service';

export const productAccessGuard: CanActivateFn = async (route) => {
  const productAccess = inject(ProductAccessService);
  const organization = inject(OrganizationService);
  const firebase = inject(FirebaseService);
  const region = inject(RegionService);
  const router = inject(Router);

  // Wait for organization to be loaded — product access evaluation needs the
  // org id and the current member doc.
  if (organization.loading) {
    try {
      await organization.isReady();
    } catch {
      // Org loading failed — safest to redirect to login rather than silently
      // allow. Returning true here could bypass access for users with stale
      // pointers to orgs they've been removed from.
      return router.createUrlTree(['/login']);
    }
  }

  // Data residency: the active org's region could not be connected — show
  // that state instead of /login (signing in again would not change it).
  if (region.error()) {
    return router.parseUrl(REGION_UNAVAILABLE_ROUTE);
  }

  const orgId = organization.data?.id;
  const uid = firebase.auth.auth.currentUser?.uid;

  // Can't evaluate access without an (org, user) pair. Route to login rather
  // than granting access.
  if (!orgId || !uid) {
    return router.createUrlTree(['/login']);
  }

  // Ensure the real-time subscription is live before we wait on its output.
  // `subscribeTo` is idempotent (returns early if already subscribed for this
  // (org, user) pair), so this is safe to call on every navigation. Without
  // this, the guard would depend on AppComponent.setupProductAccessGuard
  // having already fired — a timing race that could leave waitForAccessCheck
  // hanging indefinitely on the very first guarded navigation.
  productAccess.subscribeTo(orgId, uid);

  // Routes declare which hosted product gates them (connect also hosts the
  // chat product surface); undeclared routes are connect's own.
  const productId: string = route.data?.productId ?? PRODUCT_ID;

  const allowedProducts = await productAccess.waitForAllowedProducts();
  if (allowedProducts.includes(productId)) {
    return true;
  }

  // Denied for this product — if the org has another product this app hosts,
  // land there instead of the dead-end page (e.g. chat-only orgs hitting
  // /agents go to /chat, and vice versa).
  const fallback = Object.keys(PRODUCT_HOME_ROUTES).find(
    (id) => id !== productId && allowedProducts.includes(id),
  );
  if (fallback) {
    return router.createUrlTree([PRODUCT_HOME_ROUTES[fallback]]);
  }

  return router.createUrlTree(['/product-disabled']);
};
